import { type LegendListRef } from "@legendapp/list/react";
import {
  type EnvironmentId,
  type ModelSelection,
  type ProviderInteractionMode,
  type ThreadId,
  RuntimeMode,
} from "@cmmd-forge/contracts";
import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { collapseExpandedComposerCursor } from "../../composer-logic";
import { readEnvironmentApi } from "../../environmentApi";
import { readSavedEnvironmentBearerToken } from "../../environments/runtime/catalog";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import {
  type GitHubActionRequiredRequest,
  type GitHubToolConfirmationRequest,
} from "../../session-logic";
import { type ChatMessage, type Thread } from "../../types";
import {
  approveGitHubToolConfirmationRequest,
  buildCmmdReauthHref,
  buildGitHubConfirmationRequestContext,
  buildGitHubActionRequiredRetryPrompt,
  buildGitHubConfirmationDeclinedPrompt,
  buildGitHubConfirmationExecutedPrompt,
  buildGitHubConfirmationFailedPrompt,
  buildGitHubConfirmationRetryPrompt,
  executionRequiresCmmdReauth,
  GitHubConfirmationExecutionError,
  GitHubReauthRequiredError,
} from "../ChatView.logic";
import { toastManager } from "../ui/toast";
import { newCommandId, newMessageId } from "~/lib/utils";
import { type ChatComposerHandle } from "./ChatComposerHandle";
import { type ComposerModelSelection } from "./useComposerModelSelection";

export function useGitHubApprovalControls<ComposerTarget>(input: {
  readonly environmentId: EnvironmentId;
  readonly activeThread: Thread | undefined;
  readonly activePendingGitHubConfirmation: GitHubToolConfirmationRequest | null;
  readonly phase: string;
  readonly isSendBusy: boolean;
  readonly isConnecting: boolean;
  readonly sendInFlightRef: { current: boolean };
  readonly composerDraftTarget: ComposerTarget;
  readonly setComposerDraftPrompt: (target: ComposerTarget, prompt: string) => void;
  readonly promptRef: { current: string };
  readonly composerRef: { current: ChatComposerHandle | null };
  readonly composerModelSelection: ComposerModelSelection;
  readonly beginLocalDispatch: (options?: { readonly preparingWorktree?: boolean }) => void;
  readonly resetLocalDispatch: () => void;
  readonly runtimeMode: RuntimeMode;
  readonly interactionMode: ProviderInteractionMode;
  readonly persistThreadSettingsForNextTurn: (input: {
    readonly threadId: ThreadId;
    readonly createdAt: string;
    readonly modelSelection?: ModelSelection;
    readonly runtimeMode: RuntimeMode;
    readonly interactionMode: ProviderInteractionMode;
  }) => Promise<void>;
  readonly setThreadError: (targetThreadId: ThreadId | null, error: string | null) => void;
  readonly setOptimisticUserMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  readonly showScrollDebouncer: { current: { cancel: () => void } };
  readonly setShowScrollToBottom: (show: boolean) => void;
  readonly legendListRef: { current: LegendListRef | null };
  readonly scheduleComposerFocus: () => void;
}): {
  readonly gitHubApprovalOverlayDismissed: string | null;
  readonly setGitHubApprovalOverlayDismissed: (confirmationId: string | null) => void;
  readonly declinedGitHubConfirmationId: string | null;
  readonly isGitHubApprovalOverlayVisible: boolean;
  readonly onApproveGitHubConfirmation: (
    confirmation: GitHubToolConfirmationRequest,
  ) => Promise<void>;
  readonly onDeclineGitHubConfirmation: (
    confirmation: GitHubToolConfirmationRequest,
  ) => Promise<void>;
  readonly onGitHubApprovalFeedback: (feedbackText: string) => void;
  readonly onRetryGitHubActionRequired: (
    actionRequired: GitHubActionRequiredRequest,
  ) => Promise<void>;
} {
  const [gitHubApprovalOverlayDismissed, setGitHubApprovalOverlayDismissed] = useState<
    string | null
  >(null);
  const [declinedGitHubConfirmationId, setDeclinedGitHubConfirmationId] = useState<string | null>(
    null,
  );
  // Guards onApproveGitHubConfirmation against concurrent double-invocation (double-tap).
  const approveInFlightRef = useRef(false);
  const isGitHubApprovalOverlayVisible =
    input.activePendingGitHubConfirmation !== null &&
    input.activePendingGitHubConfirmation.confirmationId !== gitHubApprovalOverlayDismissed;

  const stagePromptInComposer = useCallback(
    (prompt: string) => {
      input.setComposerDraftPrompt(input.composerDraftTarget, prompt);
      input.promptRef.current = prompt;
      input.composerRef.current?.resetCursorState({
        cursor: collapseExpandedComposerCursor(prompt, prompt.length),
        prompt,
        detectTrigger: true,
      });
    },
    [input],
  );

  const sendPromptTurn = useCallback(
    async (prompt: string, failureMessage: string) => {
      const api = readEnvironmentApi(input.environmentId);
      if (!api || !input.activeThread) return;

      const createdAt = new Date().toISOString();
      input.sendInFlightRef.current = true;
      input.beginLocalDispatch({ preparingWorktree: false });
      input.showScrollDebouncer.current.cancel();
      input.setShowScrollToBottom(false);
      await input.legendListRef.current?.scrollToEnd?.({ animated: false });
      const messageId = newMessageId();
      input.setOptimisticUserMessages((existing) => [
        ...existing,
        {
          id: messageId,
          role: "user",
          text: prompt,
          createdAt,
          streaming: false,
        },
      ]);

      try {
        await input.persistThreadSettingsForNextTurn({
          threadId: input.activeThread.id,
          createdAt,
          modelSelection: input.composerModelSelection.modelSelection,
          runtimeMode: input.runtimeMode,
          interactionMode: input.interactionMode,
        });
        await api.orchestration.dispatchCommand({
          type: "thread.turn.start",
          commandId: newCommandId(),
          threadId: input.activeThread.id,
          message: {
            messageId,
            role: "user",
            text: prompt,
            attachments: [],
          },
          modelSelection: input.composerModelSelection.modelSelection,
          titleSeed: input.activeThread.title,
          runtimeMode: input.runtimeMode,
          interactionMode: input.interactionMode,
          createdAt,
        });
      } catch (error) {
        input.setOptimisticUserMessages((existing) =>
          existing.filter((message) => message.id !== messageId),
        );
        input.setThreadError(
          input.activeThread.id,
          error instanceof Error ? error.message : failureMessage,
        );
        // Reset local-dispatch state on FAILURE only — mirrors useChatTurnSubmission.onSend.
        // On success we intentionally leave isSendBusy up so the composer stays guarded
        // until useLocalDispatchState observes the server acknowledging the turn
        // (hasServerAcknowledgedLocalDispatch). Resetting here on success would drop
        // isSendBusy the instant the HTTP POST resolves — before the server processes
        // the turn — reopening a double-send window in onApproveGitHubConfirmation's
        // approval-retry path.
        input.resetLocalDispatch();
        throw error;
      } finally {
        input.sendInFlightRef.current = false;
      }
    },
    [input],
  );

  const handlePrompt = useCallback(
    async (
      prompt: string,
      options: {
        readonly busyToast?: {
          readonly title: string;
          readonly description: string;
        };
        readonly failureMessage: string;
        readonly throwOnFailure?: boolean;
      },
    ) => {
      if (
        input.phase === "running" ||
        input.isSendBusy ||
        input.isConnecting ||
        input.sendInFlightRef.current
      ) {
        stagePromptInComposer(prompt);
        if (options.busyToast) {
          toastManager.add({
            type: "info",
            title: options.busyToast.title,
            description: options.busyToast.description,
          });
        }
        return;
      }

      try {
        await sendPromptTurn(prompt, options.failureMessage);
      } catch (error) {
        if (options.throwOnFailure) {
          throw error;
        }
      }
    },
    [input, sendPromptTurn, stagePromptInComposer],
  );

  const onApproveGitHubConfirmation = useCallback(
    async (confirmation: GitHubToolConfirmationRequest) => {
      if (!input.activeThread) return;
      // Prevent concurrent double-invocation (double-tap / double-click).
      if (approveInFlightRef.current) return;
      approveInFlightRef.current = true;

      try {
        // Any cloud thread (the legacy `cmmd-runtime:` shape or CMMD's durable
        // `env_…` Environment, FOR-32643) keeps the confirmation row in the
        // Sprite, reachable only via the runtime router; posting to the
        // primary (window-origin) environment 404s on the master and loops.
        // Route to the thread's environment + bearer instead.
        //
        // A durable id looks up a saved bearer the same way a legacy runtime
        // id does, rather than assuming there is none. Today that lookup
        // still resolves null for a durable id: connecting a cloud
        // Environment clears any saved bearer right after persisting the
        // record (`persistConnectedCloudEnvironment` in
        // `savedCloudEnvironmentLifecycle.ts`), so no durable-shaped record
        // carries one to find. If that ever changes, this site picks the
        // bearer up on its own instead of silently never looking.
        const bearerToken = classifyEnvironmentId(input.environmentId).isCloud
          ? await readSavedEnvironmentBearerToken(input.environmentId)
          : null;
        const requestContext = buildGitHubConfirmationRequestContext({
          environmentId: input.environmentId,
          bearerToken,
        });
        const body = await approveGitHubToolConfirmationRequest({
          confirmation,
          threadId: input.activeThread.id,
          resolveUrl: requestContext.resolveUrl,
          buildFetchInit: requestContext.buildFetchInit,
        });

        const approvedConfirmation = {
          ...confirmation,
          confirmationId:
            typeof body?.confirmationId === "string"
              ? body.confirmationId
              : confirmation.confirmationId,
        };
        const executed = body?.execution && body.execution.executed === true;
        const executionFailed = body?.execution != null && body.execution.executed === false;
        if (executionFailed && executionRequiresCmmdReauth(body?.execution)) {
          // The Sprite runtime's CMMD grant expired, so the brokered action cannot mint a
          // GitHub token. Throw a typed re-auth error so the confirmation card can
          // render an actionable "Sign in to CMMD" CTA instead of relaying a
          // dead-end failure prompt to the agent.
          throw new GitHubReauthRequiredError(
            typeof body?.execution?.error === "string"
              ? body.execution.error
              : "Your CMMD session expired. Sign back in to continue.",
            buildCmmdReauthHref(),
          );
        }
        if (executed) {
          toastManager.add({
            type: "success",
            title: "GitHub action approved",
            description: "Forge ran the approved GitHub action.",
          });
        }
        if (executionFailed) {
          // Approved but the server reported the action failed to execute (non-reauth:
          // permissions, rate limit, conflict, network). Relay failure CONTEXT to the
          // agent (best-effort — don't let the relay's own failure mask the real
          // execution error), then throw so BOTH approval surfaces (inline card +
          // overlay) render a "failed" state instead of a misleading green "Approved".
          const executionError =
            typeof body?.execution?.error === "string"
              ? body.execution.error
              : "The approved GitHub action failed to execute.";
          await handlePrompt(
            buildGitHubConfirmationFailedPrompt(approvedConfirmation, executionError),
            { failureMessage: "Failed to relay GitHub action failure to the agent." },
          );
          throw new GitHubConfirmationExecutionError(executionError);
        }

        const agentPrompt = executed
          ? buildGitHubConfirmationExecutedPrompt(approvedConfirmation)
          : buildGitHubConfirmationRetryPrompt(approvedConfirmation);

        await handlePrompt(agentPrompt, {
          ...(executed
            ? {}
            : {
                busyToast: {
                  title: "GitHub action approved",
                  description:
                    "The retry instruction is ready in the composer when this turn finishes.",
                },
              }),
          failureMessage: "Failed to retry GitHub action.",
          throwOnFailure: true,
        });
      } finally {
        approveInFlightRef.current = false;
      }
    },
    // Reads input.environmentId/activeThread; dep on the whole input (matches the
    // sibling callbacks + handlePrompt's own [input] dep) to avoid a stale closure.
    [handlePrompt, input],
  );

  const onDeclineGitHubConfirmation = useCallback(
    async (confirmation: GitHubToolConfirmationRequest) => {
      if (!input.activeThread) return;
      setGitHubApprovalOverlayDismissed(confirmation.confirmationId);
      setDeclinedGitHubConfirmationId(confirmation.confirmationId);

      await handlePrompt(buildGitHubConfirmationDeclinedPrompt(confirmation), {
        failureMessage: "Failed to notify agent of declined action.",
      });
    },
    [handlePrompt, input.activeThread],
  );

  const onGitHubApprovalFeedback = useCallback(
    (feedbackText: string) => {
      stagePromptInComposer(feedbackText);
      input.scheduleComposerFocus();
    },
    [input, stagePromptInComposer],
  );

  const onRetryGitHubActionRequired = useCallback(
    async (actionRequired: GitHubActionRequiredRequest) => {
      if (!input.activeThread) return;

      await handlePrompt(buildGitHubActionRequiredRetryPrompt(actionRequired), {
        busyToast: {
          title: "GitHub setup acknowledged",
          description: "The retry instruction is ready in the composer when this turn finishes.",
        },
        failureMessage: "Failed to retry GitHub action.",
        throwOnFailure: true,
      });
    },
    [handlePrompt, input.activeThread],
  );

  return {
    gitHubApprovalOverlayDismissed,
    setGitHubApprovalOverlayDismissed,
    declinedGitHubConfirmationId,
    isGitHubApprovalOverlayVisible,
    onApproveGitHubConfirmation,
    onDeclineGitHubConfirmation,
    onGitHubApprovalFeedback,
    onRetryGitHubActionRequired,
  };
}
